import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  ApplyTemplatePresetToTermMutation,
  BulkCreateTemplatePresetItemsMutation,
  CreateTemplatePresetMutation,
  DeleteTemplatePresetMutation,
  TemplatePresetItemsQuery,
  TemplatePresetsQuery,
  UpdateTemplatePresetMutation,
} from "../graphql/templatePresets.graphql";

export interface TemplatePreset {
  templatePresetId: string;
  name: string;
  description: string | null;
  itemCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TemplatePresetItem {
  templatePresetItemId: string;
  templatePresetId: string;
  category: string;
  timeEstimate: string | null;
  itemName: string;
  question: string | null;
  note: string | null;
  isRequired: boolean;
  order: number;
}

export interface BulkTemplatePresetItemInput {
  category: string;
  itemName: string;
  question?: string | null;
  timeEstimate?: string | null;
  note?: string | null;
  isRequired?: boolean;
  order?: number;
}

export function useTemplatePresets() {
  const [result, reexecute] = useQuery({ query: TemplatePresetsQuery });

  return {
    templatePresets: (result.data?.templatePresets.nodes ??
      []) as TemplatePreset[],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useTemplatePresetItems(templatePresetId: string | null) {
  const [result, reexecute] = useQuery({
    query: TemplatePresetItemsQuery,
    variables: { templatePresetId: templatePresetId ?? "" },
    pause: !templatePresetId,
  });

  return {
    templatePresetItems: (result.data?.templatePresetItems.nodes ??
      []) as TemplatePresetItem[],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useTemplatePresetMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [, executeCreate] = useMutation(CreateTemplatePresetMutation);
  const [, executeUpdate] = useMutation(UpdateTemplatePresetMutation);
  const [, executeDelete] = useMutation(DeleteTemplatePresetMutation);
  const [, executeBulkCreateItems] = useMutation(
    BulkCreateTemplatePresetItemsMutation,
  );
  const [, executeApplyToTerm] = useMutation(ApplyTemplatePresetToTermMutation);

  const createTemplatePreset = useCallback(
    async (name: string, description?: string | null): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeCreate({ input: { name, description } });
        if (result.error) throw new Error(result.error.message);
        return true;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "テンプレートスクリプトの作成に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeCreate],
  );

  const updateTemplatePreset = useCallback(
    async (
      templatePresetId: string,
      name: string,
      description?: string | null,
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeUpdate({
          input: { templatePresetId, name, description },
        });
        if (result.error) throw new Error(result.error.message);
        return true;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "テンプレートスクリプトの更新に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdate],
  );

  const deleteTemplatePreset = useCallback(
    async (templatePresetId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeDelete({ templatePresetId });
        if (result.error) throw new Error(result.error.message);
        return result.data?.deleteTemplatePreset ?? false;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "テンプレートスクリプトの削除に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeDelete],
  );

  const bulkCreateTemplatePresetItems = useCallback(
    async (
      templatePresetId: string,
      items: readonly BulkTemplatePresetItemInput[],
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeBulkCreateItems({
          input: { templatePresetId, items: [...items] },
        });
        if (result.error) throw new Error(result.error.message);
        return true;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "テンプレートスクリプトの項目登録に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeBulkCreateItems],
  );

  const applyTemplatePresetToTerm = useCallback(
    async (templatePresetId: string, termId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeApplyToTerm({
          input: { templatePresetId, termId },
        });
        if (result.error) throw new Error(result.error.message);
        return true;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "テンプレートスクリプトの取り込みに失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeApplyToTerm],
  );

  return {
    createTemplatePreset,
    updateTemplatePreset,
    deleteTemplatePreset,
    bulkCreateTemplatePresetItems,
    applyTemplatePresetToTerm,
    loading,
    error,
  };
}
